import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import { uid } from '@fiverr-private/futile';
import { fetchImage } from '../../../lib';
import { MOCK_UP_COMPOSITION } from '../../../propTypes';
import { MOCK_UP_LAYERS, DEFAULT_ASPECT_RATIO } from '../constants';
import { layerMap } from './mapLayerComponent';
import './index.scss';

/**
 * Returns the composition initial visible layers data indexes, Gradient and background
 * @param {Object[]} layers - The mockup layers
 * @returns {Number[]}
 */
const getInitialVisibleLayersIndexes = (layers) => layers
    .filter(({ type }) => [MOCK_UP_LAYERS.GRADIENT_LAYER, MOCK_UP_LAYERS.BACKGROUND_LAYER].includes(type))
    .map(({ index }) => index);

/**
 * Trigger composition load lifecycle, has 2 phases
 * 1) Load the first image.
 * 2) Load rest of mockup images
 * After each step is done, onPhaseComplete will be emitted with the loaded assets indexes
 * @param {Object[]} layers - The mockup layers
 * @param {Function} onPhaseComplete - Callback to execute after each phase completed
 * @returns {Promise<void>}
 */
const loadComposition = async(layers, onPhaseComplete) => {
    const [first, ...restOfImages] = layers.filter(({ type }) => type === MOCK_UP_LAYERS.IMAGE_LAYER);
    await fetchImage(first);

    onPhaseComplete([first.index]);

    await Promise.all(restOfImages.map(fetchImage));

    onPhaseComplete(layers.map(({ index }) => index));
};

export const Composition = ({
    aspectRatio = DEFAULT_ASPECT_RATIO,
    layers = [],
    transparentBackground = false,
    variation,
    className
}) => {
    const displayLayers = layers.map((layer, index) => ({
        ...layer,
        index
    }));

    const [visibleLayersIndexes, setVisibleLayersIndexes] = useState(
        getInitialVisibleLayersIndexes(layers)
    );

    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        const onPhaseComplete = (indexes) => setVisibleLayersIndexes([
            ...visibleLayersIndexes,
            ...indexes
        ]);

        loadComposition(displayLayers, onPhaseComplete);
    }, []);

    return (
        <div style={{ paddingBottom: aspectRatio }} className={classNames('mock-up-composition', className)}>
            {displayLayers.map(({ src, transform, type, className, index, ...style }) => {
                const layerProps = {
                    zIndex: index + 1,
                    src,
                    transform,
                    variation,
                    className,
                    transparentBackground,
                    background: variation.backgroundColor,
                    visible: visibleLayersIndexes.includes(index),
                    ...style
                };
                if (!type) {
                    throw new Error('Layer type is not defined!');
                }
                const LayerComponent = layerMap[type];

                return (
                    <LayerComponent key={uid()} {...layerProps}/>
                );
            })}
        </div>
    );
};

Composition.propTypes = MOCK_UP_COMPOSITION;
